import React from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatLayoutProps } from '@/types/chat';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';
import { Button } from '@/components/ui/button';
import { Menu, ArrowLeft, Edit3 } from 'lucide-react';
import ChatSidebar from './ChatSidebar';

const ChatLayout: React.FC<ChatLayoutProps> = ({ 
  children, 
  sidebarOpen, 
  onSidebarToggle,
  mobileTitle = "Dashboard",
  showMobileHeader = true
}) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen: contextSidebarOpen, toggleSidebar, currentPosit } = useChatInterface();
  
  // Use prop values if provided, otherwise use context values
  const isOpen = sidebarOpen !== undefined ? sidebarOpen : contextSidebarOpen;
  const handleToggle = onSidebarToggle || toggleSidebar;
  
  // Determine if we're on dashboard pages (same logic as Navigation.tsx)
  const isDashboardPage = location.pathname.startsWith('/dashboard') ||
                         location.pathname === '/chat-history' ||
                         location.pathname === '/creator-dashboard' ||
                         location.pathname.startsWith('/p/') ||
                         location.pathname.startsWith('/public-posits') ||
                         location.pathname.startsWith('/bug-reports') ||
                         location.pathname.startsWith('/prompt-tester') ||
                         location.pathname.startsWith('/ai-testing-dashboard');
  
  // Special case: update-posit pages should show mobile header even though they're dashboard-like
  const isUpdatePositPage = location.pathname.startsWith('/update-posit/');

  // Extract positId from URL for update posit pages
  const getPositIdFromUrl = () => {
    const match = location.pathname.match(/\/update-posit\/([^\/]+)/);
    return match ? match[1] : null;
  };

  const handleBackClick = () => {
    const positId = getPositIdFromUrl();
    if (positId) {
      navigate(`/chat/${positId}`);
    } else {
      navigate(-1); // Fallback to browser back
    }
  };



  return (
    <div className="flex h-screen bg-background relative">
      {/* Sidebar - Always visible on desktop for dashboard pages, overlay on mobile */}
      {!isMobile && (
        <ChatSidebar 
          open={isDashboardPage ? true : isOpen} 
          onToggle={handleToggle}
          variant="persistent"
        />
      )}
      
      {/* Mobile overlay sidebar */}
      {isMobile && (
        <ChatSidebar 
          open={isOpen} 
          onToggle={handleToggle}
          variant="overlay"
        />
      )}
      
      {/* Main Content Area - Always offset for sidebar on desktop for dashboard pages */}
      <main className={`flex-1 flex flex-col overflow-hidden min-w-0 ${
        !isMobile && isDashboardPage ? 'ml-[280px]' : ''
      }`}>
        {/* Mobile Header with Hamburger - Show on update-posit pages and non-dashboard pages */}
        {isMobile && showMobileHeader && (!isDashboardPage || isUpdatePositPage) && (
          <div className="flex items-center justify-between p-4 border-b border-border bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            {isUpdatePositPage ? (
              <>
                {/* Back button */}
                <Button variant="ghost" size="sm" onClick={handleBackClick}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                
                {/* Left-justified title */}
                <div className="flex items-center flex-1 justify-start">
                  <h1 className="font-semibold">{currentPosit?.title || mobileTitle}</h1>
                </div>
                
                {/* Hamburger menu with outline styling */}
                <button
                  onClick={handleToggle}
                  className="inline-flex items-center justify-center p-2 rounded-[var(--radius)] text-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary bg-background border hover:bg-muted transition-colors"
                  style={{ minHeight: '40px', minWidth: '40px' }}
                >
                  <span className="sr-only">Open sidebar</span>
                  <Menu className="block h-5 w-5" aria-hidden="true" />
                </button>
              </>
            ) : (
              <>
                {/* Regular header for non-update pages */}
                <h1 className="font-semibold">{mobileTitle}</h1>
                <button
                  onClick={handleToggle}
                  className="inline-flex items-center justify-center p-2 rounded-[var(--radius)] text-foreground focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary bg-background border hover:bg-muted transition-colors"
                  style={{ minHeight: '40px', minWidth: '40px' }}
                >
                  <span className="sr-only">Open sidebar</span>
                  <Menu className="block h-5 w-5" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        )}
        
        {children}
      </main>
      
      {/* Mobile overlay backdrop when sidebar is open */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={handleToggle}
          aria-label="Close sidebar"
        />
      )}
    </div>
  );
};

export default ChatLayout; 